import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { TextInput, TocSmallText } from '@/components/widget-components'
import { BirthdayInput } from '@/components/Select/BirthdayInput'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import axiosInstance from '@/requests/axios'
import { toPeople, updateCorporate } from '@/requests/corporate'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import { TrashIcon } from '@radix-ui/react-icons'
import clsx from 'clsx'

type DirectorField =
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'occupation'
  | 'email'

interface DirectorErrors {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  occupation?: string
  email?: string
  submit?: string
}

interface Director {
  firstName: string
  lastName: string
  dateOfBirth: string
  occupation: string
  email: string
}

const initialTouchedState: Record<DirectorField, boolean> = {
  firstName: false,
  lastName: false,
  dateOfBirth: false,
  occupation: false,
  email: false,
}

const initialDirectorState: Director = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  occupation: '',
  email: '',
}

export default function DirectorInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const directors = useCorpStore((state) => state.directors)
  const updateDirector = useCorpStore((state) => state.updateDirector)
  const addDirector = useCorpStore((state) => state.addDirector)
  const removeDirector = useCorpStore((state) => state.removeDirector)
  const setDirectors = useCorpStore((state) => state.setDirectors)

  const [errors, setErrors] = useState<DirectorErrors[]>([])
  const [touched, setTouched] = useState<Record<DirectorField, boolean>[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  useEnrollmentHydration(
    useCallback(
      (enrollment) => {
        if (enrollment.directors.length === 0) {
          setErrors([])
          setTouched([])
          return
        }
        const saved = enrollment.directors.map((director) => ({
          ...initialDirectorState,
          firstName: director.firstName,
          lastName: director.lastName,
          email: director.email,
          occupation: director.occupation,
          dateOfBirth: director.dateOfBirth,
        }))
        setDirectors(saved)
        setErrors(saved.map(() => ({})))
        setTouched(saved.map(() => ({ ...initialTouchedState })))
      },
      [setDirectors]
    )
  )

  useEffect(() => {
    if (directors.length === 0) {
      setIsFormValid(false)
      return
    }

    const allFieldsFilled = directors.every(
      (director) =>
        director.firstName.trim() !== '' &&
        director.lastName.trim() !== '' &&
        director.dateOfBirth !== '' &&
        director.occupation.trim() !== '' &&
        director.email.trim() !== ''
    )

    const noErrors = errors.every(
      (directorErrors) => Object.keys(directorErrors).length === 0
    )

    setIsFormValid(allFieldsFilled && noErrors)
  }, [directors, errors])

  const validateDirector = useCallback(
    (director: Director): DirectorErrors => {
      const directorErrors: DirectorErrors = {}

      // First name validation
      if (!director.firstName.trim()) {
        directorErrors.firstName = t('directorInfo.errors.firstNameRequired')
      } else if (director.firstName.trim().length < 2) {
        directorErrors.firstName = t('directorInfo.errors.firstNameMinLength')
      } else if (!/^[a-zA-Z\s'-]+$/.test(director.firstName.trim())) {
        directorErrors.firstName = t('directorInfo.errors.firstNameInvalid')
      }

      // Last name validation
      if (!director.lastName.trim()) {
        directorErrors.lastName = t('directorInfo.errors.lastNameRequired')
      } else if (director.lastName.trim().length < 2) {
        directorErrors.lastName = t('directorInfo.errors.lastNameMinLength')
      } else if (!/^[a-zA-Z\s'-]+$/.test(director.lastName.trim())) {
        directorErrors.lastName = t('directorInfo.errors.lastNameInvalid')
      }

      // Date of birth validation
      if (!director.dateOfBirth) {
        directorErrors.dateOfBirth = t(
          'directorInfo.errors.dateOfBirthRequired'
        )
      } else {
        const birthDate = new Date(director.dateOfBirth)
        const today = new Date()
        const age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        if (isNaN(birthDate.getTime())) {
          directorErrors.dateOfBirth = t(
            'directorInfo.errors.dateOfBirthInvalid'
          )
        } else if (birthDate > today) {
          directorErrors.dateOfBirth = t(
            'directorInfo.errors.dateOfBirthFuture'
          )
        } else if (
          age < 18 ||
          (age === 18 &&
            (monthDiff < 0 ||
              (monthDiff === 0 && today.getDate() < birthDate.getDate())))
        ) {
          directorErrors.dateOfBirth = t('directorInfo.errors.ageRestriction')
        } else if (age > 120) {
          directorErrors.dateOfBirth = t('directorInfo.errors.ageUnrealistic')
        }
      }

      // Occupation validation
      if (!director.occupation.trim()) {
        directorErrors.occupation = t('directorInfo.errors.occupationRequired')
      } else if (director.occupation.trim().length < 2) {
        directorErrors.occupation = t('directorInfo.errors.occupationMinLength')
      } else if (!/^[a-zA-Z0-9\s,.-]+$/.test(director.occupation.trim())) {
        directorErrors.occupation = t('directorInfo.errors.occupationInvalid')
      }

      // Email validation
      if (!director.email.trim()) {
        directorErrors.email = t('directorInfo.errors.emailRequired')
      } else if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          director.email.trim()
        )
      ) {
        directorErrors.email = t('directorInfo.errors.emailInvalid')
      }

      return directorErrors
    },
    [t]
  )

  const handleInputChange = useCallback(
    (index: number, field: DirectorField, value: string) => {
      updateDirector(index, { [field]: value })

      const newTouched = [...touched]
      newTouched[index] = {
        ...newTouched[index],
        [field]: true,
      }
      setTouched(newTouched)

      const newErrors = [...errors]
      const directorErrors = validateDirector({
        ...directors[index],
        [field]: value,
      })
      newErrors[index] = directorErrors
      setErrors(newErrors)
    },
    [directors, errors, touched, updateDirector, validateDirector]
  )

  const handleAddDirector = useCallback(() => {
    addDirector()
    setErrors((prev) => [...prev, {}])
    setTouched((prev) => [...prev, { ...initialTouchedState }])

    setTimeout(() => {
      const newDirectorIndex = directors.length
      const newDirectorCard = document.querySelector(
        `[data-testid="director-info-card-${newDirectorIndex}"]`
      )
      if (newDirectorCard) {
        newDirectorCard.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }, 100)
  }, [addDirector])

  const handleRemoveDirector = useCallback(
    (index: number) => {
      removeDirector(index)
      setErrors((prev) => {
        const newErrors = [...prev]
        newErrors.splice(index, 1)
        return newErrors
      })

      setTouched((prev) => {
        const newTouched = [...prev]
        newTouched.splice(index, 1)
        return newTouched
      })
    },
    [removeDirector]
  )

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || isSubmitting) return

    const allErrors = directors.map((director) => validateDirector(director))
    setErrors(allErrors)

    const allTouched = directors.map(() => ({
      firstName: true,
      lastName: true,
      dateOfBirth: true,
      occupation: true,
      email: true,
    }))
    setTouched(allTouched)

    if (allErrors.every((error) => Object.keys(error).length === 0)) {
      setIsSubmitting(true)
      try {
        await updateCorporate({ directors: toPeople(directors) })

        nextState()
        navigate(ROUTES.DIRECTOR_PERSONAL_SHARE)
      } catch (error) {
        console.error('Error updating director information:', error)
        setErrors((prev) =>
          prev.map((error) => ({
            ...error,
            submit: t('directorInfo.errors.submitError'),
          }))
        )
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [
    directors,
    isFormValid,
    isSubmitting,
    validateDirector,
    nextState,
    navigate,
    t,
  ])

  // Add global keydown effect
  useEffect(() => {
    const handleGlobalKeyDown = async (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isFormValid && !isSubmitting) {
        e.preventDefault()
        await handleSubmit()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isFormValid, isSubmitting, handleSubmit])

  return (
    <div className="flex flex-col h-[600px]" data-testid="text-info-container">
      <div className="flex-none py-4 pt-0">
        <div
          className="text-lg font-normal uppercase"
          data-testid="text-info-title"
        >
          {t('directorInfo.title')}
        </div>
        <p className="mt-2 text-sm" data-testid="text-info-description">
          {t('directorInfo.description')}
        </p>
      </div>
      <div
        className="flex-1 min-h-0 overflow-y-auto mb-4"
        data-testid="text-info-list-container"
      >
        <div className="space-y-4 pr-2">
          {directors.map((director, index) => (
            <div
              key={index}
              className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg"
              data-testid={`director-info-card-${index}`}
            >
              <div className="flex justify-between items-center mb-2">
                <h3
                  className="text-lg font-medium"
                  data-testid={`director-info-card-title-${index}`}
                >
                  {t('directorInfo.directorCard.title', { number: index + 1 })}
                </h3>
                {directors.length > 1 && (
                  <button
                    onClick={() => handleRemoveDirector(index)}
                    className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                    data-testid={`director-info-remove-button-${index}`}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t('directorInfo.firstName.placeholder')}
                      value={director.firstName}
                      onChange={(e) =>
                        handleInputChange(index, 'firstName', e.target.value)
                      }
                      onBlur={() => {
                        const newTouched = [...touched]
                        newTouched[index].firstName = true
                        setTouched(newTouched)
                      }}
                      data-testid={`director-info-firstname-input-${index}`}
                    />
                    {touched[index]?.firstName && errors[index]?.firstName && (
                      <span
                        className="text-sm text-red-500"
                        data-testid={`director-info-firstname-error-${index}`}
                      >
                        {errors[index].firstName}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <TextInput
                      placeholder={t('directorInfo.lastName.placeholder')}
                      value={director.lastName}
                      onChange={(e) =>
                        handleInputChange(index, 'lastName', e.target.value)
                      }
                      onBlur={() => {
                        const newTouched = [...touched]
                        newTouched[index].lastName = true
                        setTouched(newTouched)
                      }}
                      data-testid={`director-info-lastname-input-${index}`}
                    />
                    {touched[index]?.lastName && errors[index]?.lastName && (
                      <span
                        className="text-sm text-red-500"
                        data-testid={`director-info-lastname-error-${index}`}
                      >
                        {errors[index].lastName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <BirthdayInput
                    value={director.dateOfBirth}
                    onChange={(value) =>
                      handleInputChange(index, 'dateOfBirth', value)
                    }
                    data-testid={`director-info-dob-input-${index}`}
                  />
                  {touched[index]?.dateOfBirth &&
                    errors[index]?.dateOfBirth && (
                      <span
                        className="text-sm text-red-500"
                        data-testid={`director-info-dob-error-${index}`}
                      >
                        {errors[index].dateOfBirth}
                      </span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                  <TextInput
                    placeholder={t('directorInfo.occupation.placeholder')}
                    value={director.occupation}
                    onChange={(e) =>
                      handleInputChange(index, 'occupation', e.target.value)
                    }
                    onBlur={() => {
                      const newTouched = [...touched]
                      newTouched[index].occupation = true
                      setTouched(newTouched)
                    }}
                    data-testid={`director-info-occupation-input-${index}`}
                  />
                  {touched[index]?.occupation && errors[index]?.occupation && (
                    <span
                      className="text-sm text-red-500"
                      data-testid={`director-info-occupation-error-${index}`}
                    >
                      {errors[index].occupation}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <TextInput
                    placeholder={t('directorInfo.email.placeholder')}
                    type="email"
                    value={director.email}
                    onChange={(e) =>
                      handleInputChange(index, 'email', e.target.value)
                    }
                    onBlur={() => {
                      const newTouched = [...touched]
                      newTouched[index].email = true
                      setTouched(newTouched)
                    }}
                    data-testid={`director-info-email-input-${index}`}
                  />
                  {touched[index]?.email && errors[index]?.email && (
                    <span
                      className="text-sm text-red-500"
                      data-testid={`director-info-email-error-${index}`}
                    >
                      {errors[index].email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-none mt-auto">
        <Button
          onClick={handleAddDirector}
          className="w-full flex items-center justify-center mb-4"
          data-testid="text-info-add-button"
        >
          {t('directorInfo.addButton')}
        </Button>

        {errors.some((error) => error.submit) && (
          <span
            className="text-sm text-red-500 block mb-4"
            data-testid="text-info-submit-error"
          >
            {errors.find((error) => error.submit)?.submit}
          </span>
        )}

        <div className="pb-4" data-testid="text-info-disclaimer-container">
          <TocSmallText>
            {t('directorInfo.attestation.part1')}
            <a
              href={import.meta.env.VITE_PAYTRIE_PEP_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
              data-testid="text-info-pep-link"
            >
              {t('directorInfo.attestation.pepLink')}
            </a>
            {t('directorInfo.attestation.part2')}
            <a
              href={import.meta.env.VITE_PAYTRIE_HIO_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
              data-testid="text-info-hio-link"
            >
              {t('directorInfo.attestation.hioLink')}
            </a>
            {t('directorInfo.attestation.part3')}
            <a
              href={import.meta.env.VITE_PAYTRIE_TPD_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
              data-testid="text-info-tpd-link"
            >
              {t('directorInfo.attestation.tpdLink')}
            </a>
            {t('directorInfo.attestation.part4')}
          </TocSmallText>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
          className={clsx(
            'inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
            {
              'opacity-50 cursor-not-allowed bg-gray-400':
                !isFormValid || isSubmitting,
              'hover:bg-gray-500': !isFormValid || isSubmitting,
            }
          )}
          data-testid="text-info-submit-button"
        >
          {isSubmitting
            ? t('directorInfo.submitButton.saving')
            : t('directorInfo.submitButton.continue')}
        </Button>
      </div>
    </div>
  )
}

/*
Component Improvement Suggestions:
- Add proper form labeling instead of relying on placeholders for better accessibility
- Consider making the adding/removing directors flow more intuitive with clear visual indicators
- Implement ARIA attributes for better screen reader support
- Provide more descriptive error messages with aria-live regions for real-time feedback
- Consider implementing a virtualized list if the number of directors could be large
- Add focus management for better keyboard navigation through the form
- Create logical tab order for multiple director forms
- Consider a progress indicator for multi-step forms
- Improve mobile responsiveness for the director card grid layout
- Add confirmation dialog before removing a director
*/
